import React, { createRef, PureComponent } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  MdClear as CloseIcon,
} from 'react-icons/md';
import { DetailPopupClonedRoot } from './DetailPopupClonedTarget.component';


export const POPUP_CALLER_CLASS = 'POPUP_CALLER_CLASS';
export const DETAIL_POPUP_SUPPRESS_HIDING_ON_CLICK = 'detail-popup-suppress-hiding-on-click';
export const VIRTUALIZED_SELECT_OPTION = 'Select__option';

const sizeClassNameMap = {
  sm: 'detail-popup-sm',
  md: 'detail-popup-md',
  lg: 'detail-popup-lg',
};

class DetailPopup extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node,
    ]).isRequired,

    place: PropTypes.string,
    effect: PropTypes.string,
    isCapture: PropTypes.bool,
    scrollHide: PropTypes.bool,
    event: PropTypes.string,
    headerAdditionalElements: PropTypes.arrayOf(PropTypes.node),
    size: PropTypes.oneOf(['md', 'lg']),
    onHidePopup: PropTypes.func,
    targetRef: PropTypes.node,
    className: PropTypes.string,
    offset: PropTypes.shape({
      left: PropTypes.number,
      top: PropTypes.number,
    }),
    backdrop: PropTypes.bool,
    popupRootToElevateSelector: PropTypes.string,
  };

  static defaultProps = {
    place: 'right',
    effect: 'solid',
    isCapture: true,
    scrollHide: false,
    event: 'click',
    size: 'md',
    onHidePopup: () => null,
  };

  state = {
    isVisible: false,
  };

  componentDidMount() {
    // React-tooltip prohibits closing the popup if the mouse is inside.
    // This hack overrides that.
    this.popupRef.mouseOnToolTip = () => false;
    // Provides click-away functionality for the popup. Ignore the click if inside the popup.
    this.hidePopupOnClickAway = event => {
      const ignoredElements = document.querySelector(`.${VIRTUALIZED_SELECT_OPTION}`);

      if (!event.target.parentNode) {
        return;
      }

      if (event.target === this.backdropRef
        || this.backdropRef?.contains(event.target)
        || ignoredElements?.contains(event.target)
        || event.target.parentNode.classList?.contains(VIRTUALIZED_SELECT_OPTION)
        || event.target.classList?.contains(VIRTUALIZED_SELECT_OPTION)
        // Ignore if the clicked element is inside a link, otherwise popup wouldn't open.
        || event.target.parentNode.nodeName === 'A'
        || event.target.parentNode.classList?.contains(POPUP_CALLER_CLASS)
      ) {
        return;
      }
      this.hidePopup();
    };

    document.addEventListener('click', this.hidePopupOnClickAway);
  }

  componentDidUpdate() {
    this.rebuildPopup();
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.hidePopupOnClickAway);
    if (this.props.backdrop) {
      this.hideBackdrop();
    }
  }

  backdropRef = createRef();

  showBackdrop = () => {
    this.backdropRef.classList.add('show');
  };

  hideBackdrop = () => {
    this.backdropRef.classList.remove('show');
  };

  onAfterShow = () => {
    if (this.props.backdrop) {
      this.showBackdrop();
    }
    this.setState({ isVisible: true });
  };

  hidePopup = () => {
    this.popupRef.hideTooltip();
    this.props.onHidePopup();
    if (this.props.backdrop) {
      this.hideBackdrop();
      this.setState({ isVisible: false });
    }
  };
  rebuildPopup = () => ReactTooltip.rebuild();

  createPopupRef = ref => this.popupRef = ref;

  createBackdropRef = ref => this.backdropRef = ref;

  render() {
    const {
      id,
      place,
      effect,
      isCapture,
      scrollHide,
      event,
      children,
      title,
      headerAdditionalElements,
      size,
      offset,
      className: classNameProp,
      backdrop,
      popupRootToElevateSelector,
    } = this.props;

    const popupClassName = classNames(
      'detail-popup',
      sizeClassNameMap[size],
      classNameProp
    );

    return (
      <div
        className="detail-popup-backdrop"
        ref={this.createBackdropRef}
      >
        <ReactTooltip
          id={id}
          place={place}
          effect={effect}
          isCapture={isCapture}
          scrollHide={scrollHide}
          event={event}
          className={popupClassName}
          offset={offset}
          ref={this.createPopupRef}
          afterShow={this.onAfterShow}
        >
          <div className="detail-popup-header">
            <h5 className="flex-grow-1 detail-popup-header-text">{title}</h5>
            {headerAdditionalElements}
            <button className="close" type="button" onClick={this.hidePopup}>
              <CloseIcon/>
            </button>
          </div>

          {children}
        </ReactTooltip>
        {
          this.state.isVisible && popupRootToElevateSelector && backdrop && (
            <DetailPopupClonedRoot popupRootSelector={popupRootToElevateSelector}/>
          )
        }
      </div>
    );
  }
}

export default DetailPopup;
